import dayjs from "dayjs"
import { DocumentData } from "firebase/firestore"
import { useMemo } from "react"

import { classAtoms } from "#atoms/allAtoms"
import GenericFormModal from "#blocks/Form/GenericFormModal"
import { Curriculum } from "#classes/Curriculum"
import Firebase from "#classes/Firebase"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { ROOMS } from "#constants/classOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import {
  type AddClassFormSubmitValues,
  type AddClassFormValues,
  ClassConstructor,
} from "#customTypes/composedTypes/classTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { useFirebaseTransaction } from "#hooks/reactQuery/useFirebaseMutation"
import useAnalytics from "#hooks/useAnalytics"
import { generateClassSessions } from "#utils/classHelpers"
import {
  setTimeFromString,
  STANDARD_DATE_FORMAT,
  STANDARD_TIME_FORMAT,
} from "#utils/dateHelpers"
import { getNextSemester } from "#utils/semesterHelpers"

import AddClassForm from "./AddClassForm"

const defaultClassValues = (): AddClassFormValues => ({
  isMakeupAllowed: true,
  isTryoutAllowed: true,
  isSummerCamp: false,
  capacity: "10",
  room: ROOMS.GREEN_ROOM,
  semester: getNextSemester(),
})

export interface AddClassFormModalProps {
  onClose: () => void
  classId: Uuid | undefined
}

const AddClassFormModal = ({ onClose, classId }: AddClassFormModalProps) => {
  const isEdit = classId != null
  const { trackClassAction } = useAnalytics()
  const classObj = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classId
  )
  const canSeeClasses = useHasPermission(RESOURCES.CLASS, USER_ACTIONS.READ)
  const canCreateClasses = useHasPermission(
    RESOURCES.CLASS,
    USER_ACTIONS.CREATE
  )
  const canUpdateClasses = useHasPermission(
    RESOURCES.CLASS,
    USER_ACTIONS.UPDATE
  )
  const canDeleteClasses = useHasPermission(
    RESOURCES.CLASS,
    USER_ACTIONS.DELETE
  )
  const initialValues = useMemo(() => {
    if (!classId || classObj == null || !canSeeClasses)
      return defaultClassValues()

    const classFormData: AddClassFormValues = {
      ...classObj,
      startDate: dayjs(classObj.startDate),
      times: [
        setTimeFromString(classObj.startDate, classObj.startTime),
        setTimeFromString(classObj.startDate, classObj.endTime),
      ],
      curriculumId: classObj.curriculumId,
      zoomLink: classObj.zoomLink ?? "",
      isZoomClass: !!classObj.zoomLink,
      isSummerCamp: classObj.isSummerCamp ?? false,
      isMakeupAllowed: classObj.isMakeupAllowed ?? false,
      isTryoutAllowed: classObj.isTryoutAllowed ?? false,
    }
    return classFormData
  }, [classObj, classId, canSeeClasses])

  // Errors are handled in GenericFormModal
  const handleFinish = async (cleanedValues: AddClassFormValues) => {
    if (!canUpdateClasses && isEdit) {
      throw new Error("You do not have permission to update classes")
    }

    if (!isEdit && !canCreateClasses) {
    }

    // Remove receipt fields (Receipt piggybacks on this form for validation and class data)
    delete cleanedValues.optOutDates
    delete cleanedValues.paymentAmount

    // Fetch curriculum - need fresh data since user may have changed selection in form
    // This is a one-time read in submit handler, so direct Firebase call is fine
    const curriculum =
      await Firebase.getInstance().getDocumentById<CurriculumConstructor>(
        DOC_TYPES.CURRICULUMS,
        cleanedValues.curriculumId
      )

    invariant(
      curriculum != null,
      `Curriculum is null: ${cleanedValues.curriculumId ?? "undefined"}`
    )

    const curriculumObj = new Curriculum(curriculum)
    if (curriculumObj.hasNoProjects()) {
      throw new Error(
        `Cannot save class with ${curriculumObj.getDisplayName()}. Add projects before saving a class with this curriculum.`
      )
    }

    // Leave cleanedValues untouched until this iterates on it
    const sessions = generateClassSessions(
      cleanedValues,
      // TODO: Should we use existing sessions here?
      curriculum?.numberOfSessions ?? 0
    )

    // Membership arrays and defaultTeacherId are owned by the AddXToClass
    // flows (arrayUnion/arrayRemove and explicit default-teacher handling).
    // The form's hidden fields hold a snapshot from when the modal opened, so
    // writing them back here could clobber changes made while the form was
    // open. On edit, createOrUpdateClassWithSessions rebuilds the three
    // membership arrays from the session rosters instead.
    const {
      times,
      studentIds,
      teacherIds,
      assistantIds,
      defaultTeacherId,
      ...values
    } = cleanedValues
    const [startTime, endTime] = times ?? []

    const submitData: AddClassFormSubmitValues = {
      sessions,
      classObj: {
        ...values,
        startDate: values?.startDate?.format(STANDARD_DATE_FORMAT),
        startTime: startTime?.format(STANDARD_TIME_FORMAT),
        endTime: endTime?.format(STANDARD_TIME_FORMAT),
        weekday: values?.startDate?.format("dddd"),
        sessionIds: values.sessionIds ?? [],
        // New docs still need the fields to exist; edits must not touch them.
        ...(isEdit
          ? {}
          : {
              studentIds: [],
              teacherIds: [],
              assistantIds: [],
              defaultTeacherId: defaultTeacherId ?? null,
            }),
      },
    }

    const { classId: savedClassId } =
      await Firebase.getInstance().createOrUpdateClassWithSessions(submitData)
    trackClassAction(
      isEdit ? ANALYTICS_EVENTS.CLASS_UPDATED : ANALYTICS_EVENTS.CLASS_CREATED,
      {
        classId: savedClassId,
        className: submitData.classObj.name as string,
      }
    )
  }

  const { mutate: handleTransaction } = useFirebaseTransaction()

  const handleDelete = async () => {
    if (!canDeleteClasses) {
      throw new Error("You do not have permission to delete classes")
    }

    // Delete button shouldn't show for an unsaved class anyway
    if (!initialValues?.id) return

    const studentsPromise =
      Firebase.getInstance().variableQuery<StudentConstructor>(
        DOC_TYPES.STUDENTS,
        {
          queries: {
            [`attendance.${initialValues.id}`]: {
              value: null,
              comparator: COMPARATORS.DOES_NOT_EQUAL,
            },
          },
        }
      )

    const assistantsPromise =
      Firebase.getInstance().variableQuery<StudentConstructor>(
        DOC_TYPES.STUDENTS,
        {
          queries: {
            [`assistantAttendance.${initialValues.id}`]: {
              value: null,
              comparator: COMPARATORS.DOES_NOT_EQUAL,
            },
          },
        }
      )

    const tryoutsPromise =
      Firebase.getInstance().variableQuery<TryoutConstructor>(
        DOC_TYPES.TRYOUTS,
        {
          queries: {
            classId: {
              value: initialValues.id,
              comparator: COMPARATORS.EQUALS,
            },
          },
        }
      )

    const plannedAbsencesPromise =
      Firebase.getInstance().variableQuery<MakeupConstructor>(
        DOC_TYPES.MAKEUPS,
        {
          queries: {
            missedClassId: {
              value: initialValues.id,
              comparator: COMPARATORS.EQUALS,
            },
          },
        }
      )

    const makeupsPromise =
      Firebase.getInstance().variableQuery<MakeupConstructor>(
        DOC_TYPES.MAKEUPS,
        {
          queries: {
            makeupClassId: {
              value: initialValues.id,
              comparator: COMPARATORS.EQUALS,
            },
          },
        }
      )

    // TODO: Add evals
    const evalsPromise = Firebase.getInstance().variableQuery<DocumentData>(
      DOC_TYPES.EVALUATIONS,
      {
        queries: {
          classId: {
            value: initialValues.id,
            comparator: COMPARATORS.EQUALS,
          },
        },
      }
    )

    const [students, assistants, tryouts, plannedAbsences, makeups, evals] =
      await Promise.all([
        studentsPromise,
        assistantsPromise,
        tryoutsPromise,
        plannedAbsencesPromise,
        makeupsPromise,
        evalsPromise,
      ])

    const errors: string[] = []

    if (students.length > 0) {
      const message = `Please remove this class from the following students before deleting it: 
      
${students.map((s) => s.name).join("\n")}`
      errors.push(message)
    }

    if (assistants.length > 0) {
      const message = `Please remove this class from the following assistants before deleting it: 
      
${assistants.map((a) => a.name).join("\n")}`
      errors.push(message)
    }

    if (tryouts.length > 0) {
      const message = `Please remove this class from the following tryouts before deleting it: 
      
${tryouts.map((t) => t.id).join("\n")}`
      errors.push(message)
    }

    const allMakeups = [...makeups, ...plannedAbsences]
    if (allMakeups.length > 0) {
      const message = `Please remove this class from the following makeups before deleting it: 
      
${allMakeups.map((m) => m.id).join("\n")}`
      errors.push(message)
    }

    if (evals.length > 0) {
      const message = `Please remove this class from the following evals before deleting it: 
      
${evals.map((e) => e.id).join("\n")}`
      errors.push(message)
    }

    if (errors.length > 0) {
      throw new Error(errors.join("\n\n"))
    }

    const sessions =
      initialValues?.sessionIds?.map((id) => {
        return {
          docType: DOC_TYPES.CLASS_SESSIONS,
          id,
          data: null,
          action: "delete",
        } as const
      }) ?? []

    await handleTransaction([
      {
        docType: DOC_TYPES.CLASSES,
        id: initialValues.id,
        data: null,
        action: "delete",
      },
      ...sessions,
    ])

    trackClassAction(ANALYTICS_EVENTS.CLASS_DELETED, {
      classId: initialValues.id,
      className: initialValues.name as string,
    })

    // Brief delay to allow Firebase listeners to process the deletion
    setTimeout(() => {
      onClose()
    }, 500)
  }

  return (
    <GenericFormModal<AddClassFormValues, AddClassFormValues>
      title={
        isEdit && initialValues?.name
          ? `Class: ${initialValues.name}`
          : "Add new class"
      }
      onClose={onClose}
      initialValues={initialValues}
      onFinish={handleFinish}
      resource={RESOURCES.CLASS}
      onDelete={handleDelete}
    >
      <AddClassForm initialValues={initialValues} />
    </GenericFormModal>
  )
}

export default AddClassFormModal
